import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import markdownStyles from '../markdown-styles.module.css';
import { getInitials } from '../constants';
import constants, { BASE_URL, BASE_WORDURL } from '../constants';
import { fetchActiveShortcutsByTask, executeShortcut } from '../../slice/shortcutsSlice';
import { updateAnswerStatusAsync } from '../../slice/taskStatusSlice';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useTranslation } from '../../i18n/LanguageContext';
import { authHeader } from '../../utils/authHeaders';
import { downloadAuthenticatedFile } from '../../utils/downloadFile';
import { playSpeech } from '../../utils/ttsPlayer';
import { toast } from 'react-hot-toast';
import { Download } from 'lucide-react';

const ANSWER_STATUS_KEYS = [
  { key: 'accepted', color: '#10B981', textColor: '#ffffff' },
  { key: 'review', color: '#F59E0B', textColor: '#ffffff' },
  { key: 'missing', color: '#F97316', textColor: '#ffffff' },
  { key: 'rejected', color: '#EF4444', textColor: '#ffffff' },
];

const TTS_VOICE = 'de-DE-SeraphinaMultilingualNeural';

const CommentSection = ({ user, answer, taskId, historyId, onStatusChange, existingStatus, showStatus = false, sqId = null, generatedDocumentId = null, commentCount = 0, commentsOpen = false, onToggleComments = null }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { activeShortcuts } = useSelector(state => state.shortcuts);
  const { aispaceColor } = useThemeColor();

  const ANSWER_STATUSES = ANSWER_STATUS_KEYS.map(s => ({
    ...s,
    label: t(`answerStatus.${s.key}`),
  }));
  const [activeButton, setActiveButton] = useState(null);
  const [localExecuting, setLocalExecuting] = useState(false);

  // Speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isQASpeaking, setIsQASpeaking] = useState(false);
  const [isQALoading, setIsQALoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const speechRef = useRef(null);
  const qaSpeechRef = useRef(null);

  // Shortcuts state
  const [showShortcutsDropdown, setShowShortcutsDropdown] = useState(false);
  const [shortcutResult, setShortcutResult] = useState(null);
  const [showTransformedContent, setShowTransformedContent] = useState(false);
  const shortcutsDropdownRef = useRef(null);

  // Answer status state
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [pendingStatusKey, setPendingStatusKey] = useState(null);
  const statusDropdownRef = useRef(null);

  // Initialize with existing status if provided
  useEffect(() => {
    if (existingStatus) {
      const match = ANSWER_STATUSES.find(s => s.key === existingStatus || s.label === existingStatus);
      if (match) setSelectedStatus(match);
    }
  }, [existingStatus]);

  // Handle click outside status dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusDropdown]);

  const handleStatusSelect = async (status) => {
    // Close dropdown immediately; keep previously selected status visible until save succeeds
    setShowStatusDropdown(false);

    // No backend linkage → apply locally without a network round-trip
    if (!historyId || !sqId) {
      setSelectedStatus(status);
      if (onStatusChange && typeof onStatusChange === 'function') {
        onStatusChange(status.key);
      }
      console.warn('[AnswerStatus] Saved locally only — missing linkage:', { historyId, sqId });
      return;
    }

    setIsStatusSaving(true);
    setPendingStatusKey(status.key);
    try {
      await dispatch(updateAnswerStatusAsync({
        history_id: historyId,
        sq_id: sqId,
        answer_status: status.key
      })).unwrap();
      // Only now reflect the new status in the UI
      setSelectedStatus(status);
      if (onStatusChange && typeof onStatusChange === 'function') {
        onStatusChange(status.key);
      }
    } catch (err) {
      console.error('[AnswerStatus] save failed:', err);
    } finally {
      setIsStatusSaving(false);
      setPendingStatusKey(null);
    }
  };

  // Fetch active shortcuts for the task
  useEffect(() => {
    if (taskId) {
      dispatch(fetchActiveShortcutsByTask(taskId));
    }
  }, [dispatch, taskId]);

  // Handle click outside shortcuts dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shortcutsDropdownRef.current && !shortcutsDropdownRef.current.contains(event.target)) {
        setShowShortcutsDropdown(false);
      }
    };
    if (showShortcutsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShortcutsDropdown]);

  // Handle shortcut selection - use result directly from the dispatch, not global state
  const handleShortcutSelect = async (shortcut) => {
    setShowShortcutsDropdown(false);
    setActiveButton('shortcut');
    setLocalExecuting(true);
    try {
      const result = await dispatch(executeShortcut({
        shortcutId: shortcut.shortcut_id,
        content: answer,
        historyId: historyId
      })).unwrap();
      setShortcutResult(result);
      setShowTransformedContent(true);
    } catch (error) {
      console.error('Shortcut execution error:', error);
      setActiveButton(null);
    } finally {
      setLocalExecuting(false);
    }
  };

  // Close transformed content
  const handleCloseTransformed = () => {
    setShowTransformedContent(false);
    setShortcutResult(null);
    setActiveButton(null);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(answer);
    console.log('Answer copied to clipboard');
  };

  // Strip markdown symbols so TTS reads clean text (not "star star bold star star")
  const stripMarkdown = (md) => {
    if (!md) return '';
    return md
      .replace(/```[\s\S]*?```/g, '')       // code blocks
      .replace(/`([^`]+)`/g, '$1')          // inline code
      .replace(/!\[.*?\]\(.*?\)/g, '')      // images
      .replace(/\[([^\]]+)\]\(.*?\)/g, '$1') // links → text only
      .replace(/^#{1,6}\s+/gm, '')          // headings
      .replace(/(\*\*|__)(.*?)\1/g, '$2')   // bold
      .replace(/(\*|_)(.*?)\1/g, '$2')      // italic
      .replace(/~~(.*?)~~/g, '$1')          // strikethrough
      .replace(/^[\s]*[-*+]\s+/gm, '')      // unordered list bullets
      // Strip markdown ordered list numbers (e.g. "1. ", "2. "), but preserve
      // dates like "23. Januar" / "15. March" by requiring the number NOT
      // be followed by a month name.
      .replace(
        /^[\s]*\d+\.\s+(?!(?:Januar|Februar|März|Maerz|April|Mai|Juni|Juli|August|September|Oktober|November|Dezember|January|February|March|May|June|July|October|December)\b)/gm,
        ''
      )
      .replace(/^>\s+/gm, '')               // blockquotes
      .replace(/---+|===+|\*\*\*+/g, '')    // horizontal rules
      .replace(/\|/g, ' ')                  // table pipes
      .replace(/\n{3,}/g, '\n\n')           // excess newlines
      .trim();
  };

  const stopSpeech = () => {
    speechRef.current?.stop();
    speechRef.current = null;
    setIsSpeaking(false);
    setIsLoading(false);
    setActiveButton(prev => (prev === 'tts' ? null : prev));
  };

  const handleSpeak = () => {
    if (isLoading) return; // prevent multiple clicks during loading
    if (isSpeaking) {
      stopSpeech();
      return;
    }

    // Stop any previous audio before starting new
    speechRef.current?.stop();

    const text = stripMarkdown(String(answer || ''));
    if (!text.trim()) return;

    setIsLoading(true);
    setActiveButton('tts');

    speechRef.current = playSpeech(text, TTS_VOICE, {
      onPlaying: () => {
        setIsLoading(false);
        setIsSpeaking(true);
      },
      onEnd: () => {
        speechRef.current = null;
        setIsSpeaking(false);
        setActiveButton(prev => (prev === 'tts' ? null : prev));
      },
      onError: (error) => {
        console.error('TTS error:', error);
        speechRef.current = null;
        setIsLoading(false);
        setIsSpeaking(false);
        setActiveButton(null);
      },
    });
  };

  const handleQASpeak = () => {
    if (isQALoading) return; // prevent multiple clicks
    const text = stripMarkdown(String(shortcutResult?.transformed_content || ''));
    if (!text.trim()) return;
    if (isQASpeaking) {
      qaSpeechRef.current?.stop();
      qaSpeechRef.current = null;
      setIsQASpeaking(false);
      return;
    }
    qaSpeechRef.current?.stop();
    setIsQALoading(true);
    qaSpeechRef.current = playSpeech(text, TTS_VOICE, {
      onPlaying: () => {
        setIsQALoading(false);
        setIsQASpeaking(true);
      },
      onEnd: () => {
        qaSpeechRef.current = null;
        setIsQASpeaking(false);
      },
      onError: (error) => {
        console.error('QA TTS error:', error);
        qaSpeechRef.current = null;
        setIsQALoading(false);
        setIsQASpeaking(false);
      },
    });
  };

  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, []);
  
  const handleDownloadGeneratedDoc = (format) => {
    const name = format === 'pdf' ? 'document.pdf' : 'document.docx';
    downloadAuthenticatedFile(`${BASE_WORDURL}/download/${generatedDocumentId}?format=${format}`, name)
      .catch(() => toast.error(t('chatActions.wordDownloadError')));
  };

  return (
    <div className="mt-4">
      {/* Action buttons row - matching Figma design */}
      <div className="flex items-center gap-2 mb-4">
        {/* Speaker button */}
        <button
          onClick={handleSpeak}
          disabled={isLoading}
          title={isLoading ? t('chatActions.loading') : isSpeaking ? t('chatActions.stop') : t('chatActions.listen')}
          className={`p-1.5 rounded transition-colors ${
            activeButton === 'tts'
              ? 'bg-[#F3F4F6]'
              : 'hover:bg-[#F3F4F6]'
          } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
        >
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-[#6B7280]" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          ) : (
            <img src={constants.speaker} alt="Audio" className="h-5 w-5 opacity-60" />
          )}
        </button>

        {/* Copy button */}
        <button
          onClick={() => {
            handleCopy();
            setActiveButton('copy');
            setTimeout(() => {
              setActiveButton(prev => (prev === 'copy' ? null : prev));
            }, 1500);
          }}
          title={t('chatActions.copy')}
          className={`p-1.5 rounded transition-colors ${
            activeButton === 'copy'
              ? 'bg-[#F3F4F6]'
              : 'hover:bg-[#F3F4F6]'
          }`}
        >
          <img src={constants.copyimage} alt="Copy" className="h-5 w-5 opacity-60" />
        </button>

        {onToggleComments && (
          <div
            className={`relative p-1.5 rounded transition-colors cursor-pointer ${commentsOpen ? '' : 'hover:bg-[#F3F4F6]'}`}
            onClick={onToggleComments}
            title={t('chatActions.addComment')}
            style={commentsOpen ? { backgroundColor: aispaceColor } : {}}
          >
            <img
              src={constants.commentimage}
              alt="Comment"
              className={`h-5 w-5 ${commentsOpen ? 'brightness-0 invert' : 'opacity-60'}`}
            />
            {commentCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-semibold flex items-center justify-center"
                style={{ backgroundColor: aispaceColor }}
              >
                {commentCount}
              </span>
            )}
          </div>
        )}

        {/* Quick Actions Dropdown */}
        <div className="relative" ref={shortcutsDropdownRef}>
          <button
            onClick={() => setShowShortcutsDropdown(!showShortcutsDropdown)}
            disabled={localExecuting}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-[13px] font-medium transition-colors ${
              (showShortcutsDropdown || activeButton === 'shortcut')
                ? 'text-white'
                : 'text-[#6B7280] hover:bg-[#F3F4F6]'
            } ${localExecuting ? 'opacity-50 cursor-wait' : ''}`}
            style={(showShortcutsDropdown || activeButton === 'shortcut') ? { backgroundColor: aispaceColor } : {}}
          >
            {localExecuting ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={(showShortcutsDropdown || activeButton === 'shortcut') ? '' : 'opacity-60'}>
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
                <span>{t('chatActions.quickActions')}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </>
            )}
          </button>

          {/* Dropdown Menu — opens upward so all options stay in the visible
              chat area instead of being clipped beneath the action toolbar.
              Also opens for a Word-generation result even when this agent
              has no shortcuts configured at all (generatedDocumentId alone
              is enough) — the two entries above are the ONLY place Download
              Word/PDF live, no separate buttons in the bubble itself. */}
          {showShortcutsDropdown && ((activeShortcuts && activeShortcuts.length > 0) || generatedDocumentId) && (
            <div className="absolute left-0 bottom-full mb-1 w-56 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 py-1">
              {generatedDocumentId && (
                <>
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F9FAFB] flex items-center gap-3 transition-colors text-inherit"
                    onClick={() => { setShowShortcutsDropdown(false); handleDownloadGeneratedDoc('docx'); }}
                  >
                    <Download size={18} className="text-[#6B7280] flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-[14px] font-medium text-[#374151] truncate">
                      {t('chatActions.wordDownloadWord')}
                    </div>
                  </button>
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F9FAFB] flex items-center gap-3 transition-colors text-inherit"
                    onClick={() => { setShowShortcutsDropdown(false); handleDownloadGeneratedDoc('pdf'); }}
                  >
                    <Download size={18} className="text-[#6B7280] flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-[14px] font-medium text-[#374151] truncate">
                      {t('chatActions.wordDownloadPdf')}
                    </div>
                  </button>
                  {activeShortcuts && activeShortcuts.length > 0 && (
                    <div className="my-1 border-t border-[#E5E7EB]" />
                  )}
                </>
              )}
              {activeShortcuts && activeShortcuts.map((shortcut) => (
                <button
                  key={shortcut.shortcut_id}
                  onClick={() => handleShortcutSelect(shortcut)}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#F9FAFB] flex items-center gap-3 transition-colors"
                >
                  <span className="text-lg">{shortcut.icon || '✨'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium text-[#374151] truncate">{shortcut.name}</div>
                    <div className="text-[12px] text-[#9CA3AF] truncate">{shortcut.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Answer Status Dropdown - only for Agent questions */}
        {showStatus && (
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => !isStatusSaving && setShowStatusDropdown(!showStatusDropdown)}
            disabled={isStatusSaving}
            title={isStatusSaving ? t('chatActions.saving') : t('chatActions.setStatus')}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-[13px] font-medium transition-colors border ${isStatusSaving ? 'opacity-70 cursor-wait' : ''}`}
            style={selectedStatus ? {
              backgroundColor: selectedStatus.color,
              color: selectedStatus.textColor,
              borderColor: selectedStatus.color,
            } : {
              backgroundColor: '#ffffff',
              color: '#6B7280',
              borderColor: '#E5E7EB',
            }}
          >
            {isStatusSaving ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              !selectedStatus && (
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: '#D1D5DB' }}
                />
              )
            )}
            <span className="max-w-[180px] truncate">
              {isStatusSaving
                ? t('chatActions.saving')
                : (selectedStatus ? selectedStatus.label : t('chatActions.statusSelect'))}
            </span>
            {!isStatusSaving && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </button>

          {showStatusDropdown && (
            <div className="absolute left-0 bottom-full mb-1 w-64 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 py-1">
              {ANSWER_STATUSES.map((status) => {
                const isPending = pendingStatusKey === status.key;
                const isSelected = selectedStatus && selectedStatus.key === status.key;
                return (
                  <button
                    key={status.key}
                    onClick={() => !isStatusSaving && handleStatusSelect(status)}
                    disabled={isStatusSaving}
                    className={`w-full px-4 py-2.5 text-left hover:bg-[#F9FAFB] flex items-center gap-3 transition-colors ${isStatusSaving ? 'cursor-wait' : ''} ${isStatusSaving && !isPending ? 'opacity-50' : ''}`}
                  >
                    <span
                      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="text-[14px] font-medium text-[#374151] truncate">
                      {status.label}
                    </span>
                    {isPending ? (
                      <svg className="ml-auto animate-spin w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" stroke={status.color}>
                        <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill={status.color} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : isSelected && (
                      <svg
                        className="ml-auto w-4 h-4 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={status.color}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Transformed Content Section - hidden when comment is active */}
      {showTransformedContent && shortcutResult && activeButton !== 'comment' && (
        <div className="mb-4 border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="flex items-center justify-between bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚡</span>
              <span className="font-medium text-[#374151]">{shortcutResult.shortcut_name || t('chatActions.quickActionsResult')}</span>
            </div>
            <div className="flex items-center gap-1">
              {/* TTS for transformed content — uses separate state */}
              <button
                onClick={handleQASpeak}
                disabled={isQALoading}
                className={`text-[#9CA3AF] hover:text-[#374151] p-1 transition-colors ${isQALoading ? 'opacity-50 cursor-wait' : ''}`}
                title={t('chatActions.readAloud')}
              >
                {isQALoading ? (
                  <svg className="animate-spin w-5 h-5 text-[#6B7280]" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                ) : isQASpeaking ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  </svg>
                )}
              </button>
              <button
                onClick={handleCloseTransformed}
                className="text-[#9CA3AF] hover:text-[#6B7280] p-1"
                title={t('chatActions.close')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className={`text-[#374151] max-h-64 overflow-y-auto ${markdownStyles.reactMarkDown}`}>
              {/* Inline-override paragraph/list/table sizes so this matches the
                  AI answer (ContentRenderer applies the same 14.4px overrides).
                  Without these, `.reactMarkDown>*` forces 16px and the shortcut
                  output reads larger than the answer above it. */}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  p: ({ node, ...props }) => {
                    const isInListItem = node?.parent && node.parent.tagName === 'li';
                    return <p style={isInListItem ? { fontWeight: 'normal', fontSize: '14.4px' } : { fontSize: '14.4px' }} {...props} />;
                  },
                  li: ({ node, ...props }) => <li style={{ fontWeight: 'normal', fontSize: '14.4px' }} {...props} />,
                  table: ({ node, ...props }) => <table className="border-collapse border border-gray-300 my-4 text-[14.4px]" {...props} />,
                  thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                  tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
                  th: ({ node, ...props }) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-[14.4px]" {...props} />,
                  td: ({ node, ...props }) => <td className="border border-gray-300 px-4 py-2 text-[14.4px]" {...props} />,
                }}
              >
                {shortcutResult.transformed_content || ''}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CommentSection;